// src/components/SEO.js
import { Helmet } from 'react-helmet-async';

const SEO = () => {
  const seoData = {

    title: "NexaProxy | Premium Proxy Solutions for Global Connectivity",
    description: "Enterprise-grade proxy solutions offering DataCenter, Residential, and Elite proxies. Secure, reliable, and undetectable with zero Scamalytics score. Perfect for business automation and data collection.",
    
    // Social Media & OG Tags
    ogTitle: "NexaProxy - Advanced Proxy Solutions | DC, Residential & Elite Proxies",
    ogDescription: "Premium proxy network with 330K+ servers across 190+ countries. Ideal for businesses, developers, and automation needs. Starting at $1/week.",
    
    // Keywords
    keywords: [
      "proxy service",
      "residential proxies",
      "datacenter proxies",
      "elite proxies",
      "business proxy solutions",
      "undetectable proxies",
      "zero scamalytics score",
      "rotating proxies",
      "dedicated proxies",
      "proxy for PayPal",
      "proxy for Amazon",
      "proxy for Outlier",
      "HTTP proxy",
      "SOCKS5 proxy",
      "proxy API",
      "global proxy network",
      "anonymous proxies",
      "proxy server",
      "IP rotation service",
      "proxy provider"
    ].join(", "),

    // URL & Domain Info
    url: "https://nexaproxy.com",
    canonicalUrl: "https://nexaproxy.com",

    // Rich Snippets / Structured Data
    organization: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "NexaProxy",
      url: "https://nexaproxy.com",
      logo: "https://nexaproxy.com/nexapx_logo.png",
      description: "Premium proxy network provider offering secure and reliable proxy solutions for businesses and developers.",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+254-705-415-499",
        contactType: "customer service",
        availableLanguage: ["English"]
      },
      sameAs: [
        "https://headwayapp.co/nexaproxy-changelog"
      ]
    },

    product: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "NexaProxy Proxy Services",
      description: "Enterprise-grade proxy solutions with global coverage and zero detection rates.",
      brand: {
        "@type": "Brand",
        name: "NexaProxy"
      },
      offers: {
        "@type": "AggregateOffer",
        lowPrice: "1",
        highPrice: "10",
        priceCurrency: "USD",
        offerCount: "3",
        offers: [
          {
            "@type": "Offer",
            name: "DataCenter Proxies",
            price: "1",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2024-12-31"
          },
          {
            "@type": "Offer",
            name: "Residential Proxies",
            price: "6",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2024-12-31"
          },
          {
            "@type": "Offer",
            name: "Elite Proxies",
            price: "10",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2024-12-31"
          }
        ]
      }
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={seoData.keywords} />
      <link rel="canonical" href={seoData.canonicalUrl} />

      {/* Language and Region */}
      <meta property="og:locale" content="en_US" />
      <meta httpEquiv="content-language" content="en-us" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seoData.ogTitle} />
      <meta property="og:description" content={seoData.ogDescription} />
      <meta property="og:url" content={seoData.url} />
      <meta property="og:site_name" content="NexaProxy" />
      <meta property="og:image" content={`${seoData.url}/og-image.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.ogTitle} />
      <meta name="twitter:description" content={seoData.ogDescription} />
      <meta name="twitter:image" content={`${seoData.url}/og-image.jpg`} />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="theme-color" content="#ff6b00" />
      <meta name="rating" content="safe for kids" />
      <meta name="copyright" content={`© ${new Date().getFullYear()} NexaProxy`} />
      <meta name="author" content="NexaProxy" />

      {/* Mobile Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="format-detection" content="telephone=no" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(seoData.organization)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(seoData.product)}
      </script>

      {/* Preconnect to Important Domains */}
      <link rel="preconnect" href="https://nexaproxy.com" />
      <link rel="dns-prefetch" href="https://nexaproxy.com" />
      
      {/* PWA Tags */}
      <link rel="manifest" href="/manifest.json" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="NexaProxy" />
    </Helmet>
  );
};

export default SEO;